import { UserRole, UserTier } from '../../../lib/contracts';
import { toUserRole } from '../rbac';
import type { AuthUser, MeResponse, MeUserPayload } from '../types/auth.types';
import { resolveCanonicalPrincipal } from '../../../../shared/identity';

const normalizeText = (value: unknown) => String(value ?? '').replace(/\u0000/g, '').trim();

const resolveMeUser = (payload: MeResponse): MeUserPayload | undefined => {
  const payloadData = payload?.data;
  if (payloadData && typeof payloadData === 'object' && 'user' in payloadData && payloadData.user) {
    return payloadData.user as MeUserPayload;
  }

  const directDataUser = payloadData as MeUserPayload | undefined;
  if (directDataUser && (directDataUser._id || directDataUser.id || directDataUser.userId)) {
    return directDataUser;
  }

  if (payload?.user && (payload.user._id || payload.user.id || payload.user.userId)) {
    return payload.user;
  }

  return undefined;
};

const normalizeBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
};

const normalizeNumeric = (value: unknown, fallback = 0) => {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : fallback;
};

const normalizeDisplay = (value: unknown, fallback: string) => {
  const normalized = String(value || '').trim();
  return normalized || fallback;
};

const normalizeEmail = (value: unknown) => {
  const normalized = normalizeText(value).toLowerCase();
  if (!normalized) return undefined;
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(normalized) ? normalized : undefined;
};

const normalizeIsoDate = (value: unknown): string | undefined => {
  const normalized = normalizeText(value);
  if (!normalized) return undefined;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

const normalizeStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  return value.map(entry => normalizeText(entry)).filter(Boolean);
};

const normalizeTeenStatus = (value: unknown): AuthUser['teenStatus'] | undefined => {
  const normalized = normalizeText(value);
  if (normalized === 'pending_approval' || normalized === 'active' || normalized === 'suspended') {
    return normalized;
  }
  return undefined;
};

const normalizeParentControls = (value: unknown): AuthUser['parentControls'] | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const controls = value as Record<string, unknown>;
  return {
    dailySpendingLimitCents: normalizeNumeric(controls.dailySpendingLimitCents),
    perOrderSpendingLimitCents: normalizeNumeric(controls.perOrderSpendingLimitCents),
    allowTeenPersonalCards: normalizeBoolean(controls.allowTeenPersonalCards),
    requirePreApproval: normalizeBoolean(controls.requirePreApproval),
    allowedStoreHubs: normalizeStringArray(controls.allowedStoreHubs) || [],
    lockedStoreHubs: normalizeStringArray(controls.lockedStoreHubs) || []
  };
};

const normalizeTeenDailySpent = (value: unknown): AuthUser['teenDailySpent'] | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const spent = value as Record<string, unknown>;
  const date = normalizeText(spent.date);
  if (!date) return undefined;
  return {
    date,
    amountCents: normalizeNumeric(spent.amountCents)
  };
};

const allowedTiers = new Set<UserTier>([UserTier.COMMON, UserTier.BRONZE, UserTier.SILVER, UserTier.GOLD, UserTier.PLATINUM, UserTier.GREEN]);

const TIER_RANK: Record<UserTier, number> = {
  [UserTier.COMMON]: 0,
  [UserTier.BRONZE]: 1,
  [UserTier.SILVER]: 2,
  [UserTier.GOLD]: 3,
  [UserTier.PLATINUM]: 4,
  [UserTier.GREEN]: 5
};

const normalizeTier = (...rawCandidates: unknown[]): UserTier => {
  const candidates = rawCandidates
    .map((entry) => normalizeText(entry).toUpperCase())
    .filter(Boolean)
    .map((entry) => (entry === 'NONE' ? 'COMMON' : entry));

  for (const candidate of candidates) {
    if (allowedTiers.has(candidate as UserTier)) return candidate as UserTier;
  }

  return UserTier.COMMON;
};

const resolveHighestTier = (...rawCandidates: unknown[]): UserTier => {
  let highest: UserTier = UserTier.COMMON;
  let highestRank = 0;
  for (const raw of rawCandidates) {
    const candidate = normalizeTier(raw);
    const rank = TIER_RANK[candidate] ?? 0;
    if (rank > highestRank) {
      highest = candidate;
      highestRank = rank;
    }
  }
  return highest;
};

const normalizeIdentity = (u: MeUserPayload) => {
  const principal = resolveCanonicalPrincipal({
    _id: u._id,
    id: u.id,
    userId: u.userId,
    customerId: u.customerId as unknown
  });
  const rawId = normalizeText(principal?.id);
  return {
    _id: rawId,
    id: rawId
  };
};

export const normalizeAuthUserRecord = (u: MeUserPayload | null | undefined): AuthUser | null => {
  if (!u) return null;

  const assignedTier = resolveHighestTier(u.tier, u.membershipTier);
  const tier = resolveHighestTier(u.effectiveTier, assignedTier);
  const mappedRole = toUserRole(u.role) || UserRole.CUSTOMER;
  const rawRoles = Array.isArray(u.roles) && u.roles.length > 0 ? u.roles : [u.role];
  const mappedRoles = Array.from(
    new Set(rawRoles.map((r) => toUserRole(r)).filter((r): r is UserRole => r !== null))
  );
  if (!mappedRoles.includes(mappedRole)) {
    mappedRoles.push(mappedRole);
  }
  const identity = normalizeIdentity(u);
  if (!identity.id) return null;

  const user: AuthUser = {
    ...identity,
    name: normalizeDisplay(u.name || u.displayName || u.username, 'USER'),
    username: normalizeDisplay(u.username, normalizeDisplay(u.name || u.displayName, 'user')),
    email: normalizeEmail(u.email),
    role: mappedRole,
    roles: mappedRoles,
    creditBalance: normalizeNumeric(u.creditBalance),
    loyaltyPoints: normalizeNumeric(u.loyaltyPoints),
    tier,
    assignedTier,
    membershipTier: tier,
    ordersCompleted: normalizeNumeric(u.ordersCompleted),
    phoneVerified: normalizeBoolean(u.phoneVerified),
    photoIdVerified: normalizeBoolean(u.photoIdVerified),
    dateOfBirth: normalizeIsoDate(u.dateOfBirth),
    createdAt: normalizeIsoDate(u.createdAt)
  };

  if (u.isTeenAccount !== undefined) user.isTeenAccount = normalizeBoolean(u.isTeenAccount);
  const parentId = normalizeText(u.parentId);
  if (parentId) user.parentId = parentId;
  const teenStatus = normalizeTeenStatus(u.teenStatus);
  if (teenStatus) user.teenStatus = teenStatus;
  const parentEmail = normalizeEmail(u.parentEmail);
  if (parentEmail) user.parentEmail = parentEmail;
  const parentControls = normalizeParentControls(u.parentControls);
  if (parentControls) user.parentControls = parentControls;
  const teenDailySpent = normalizeTeenDailySpent(u.teenDailySpent);
  if (teenDailySpent) user.teenDailySpent = teenDailySpent;
  const psaVersion = normalizeText(
    (u as MeUserPayload).personalShopperAuthorization?.version
  );
  if (psaVersion) {
    user.personalShopperAuthorization = {
      version: psaVersion,
      acceptedAt: normalizeIsoDate((u as MeUserPayload).personalShopperAuthorization?.acceptedAt)
    };
  }

  return user;
};

export const normalizeSessionUser = (payload: MeResponse): AuthUser | null =>
  normalizeAuthUserRecord(resolveMeUser(payload));
